import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Fab,
  Slide,
  InputAdornment,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  Minimize2,
  Maximize2,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { sendChatMessage } from '../../services/aiService';

function GlobalAIChat({ onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: "Hi! I'm your AI assistant. I can help you with jobs, estimates, permits, inventory, and more. What would you like to do?",
      suggestions: ['Create a new job', 'Check production status', 'View inventory', 'Generate report'],
    },
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      role: 'user',
      content: message,
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = message;
    setMessage('');
    setIsLoading(true);

    try {
      const response = await sendChatMessage(
        currentMessage,
        'general',
        messages.map(m => ({ role: m.role, content: m.content }))
      );

      const assistantMessage = {
        id: messages.length + 2,
        role: 'assistant',
        content: response.message,
        actions: response.actions,
        suggestions: response.suggestions,
        intent: response.intent,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [
        ...prev,
        {
          id: messages.length + 2,
          role: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessage(suggestion);
    setTimeout(() => {
      const input = document.querySelector('#ai-chat-input');
      if (input) input.focus();
    }, 100);
  };

  const handleActionClick = (action) => {
    if (action.type === 'navigate' && onNavigate) {
      onNavigate(action.target);
      setIsMinimized(true);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: 1,
        role: 'assistant',
        content: "Chat cleared! How can I help you today?",
        suggestions: ['Create a new job', 'Check production status', 'View inventory', 'Generate report'],
      },
    ]);
  };

  if (!isOpen) {
    return (
      <Tooltip title="AI Assistant">
        <Fab
          color="primary"
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
            },
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
          }}
        >
          <MessageCircle size={24} />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Slide direction="up" in={isOpen}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: isMinimized ? 320 : 420,
          height: isMinimized ? 64 : 560,
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => isMinimized && setIsMinimized(false)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)' }}>
              <Sparkles size={20} />
            </Avatar>
            <Box>
              <Typography fontWeight={600} fontSize={15}>AI Assistant</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: 11 }}>
                {isLoading ? 'Thinking...' : 'Online'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Clear chat">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleClearChat(); }}
                sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              sx={{ color: 'white', opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </Box>

        {!isMinimized && (
          <>
            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: '#FAFAFA',
              }}
              className="scrollbar-thin"
            >
              {messages.map((msg) => (
                <Box key={msg.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                        fontSize: 14,
                      }}
                    >
                      {msg.role === 'user' ? 'U' : <Sparkles size={16} />}
                    </Avatar>
                    <Box
                      sx={{
                        maxWidth: '80%',
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                        color: msg.role === 'user' ? 'white' : 'text.primary',
                        boxShadow: msg.role === 'assistant' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      }}
                    >
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {msg.content}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  {msg.actions && msg.actions.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 5, flexWrap: 'wrap' }}>
                      {msg.actions.map((action, idx) => (
                        <Button
                          key={idx}
                          size="small"
                          variant="outlined"
                          startIcon={<ExternalLink size={14} />}
                          onClick={() => handleActionClick(action)}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Box>
                  )}

                  {/* Suggestions */}
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, ml: 5, flexWrap: 'wrap' }}>
                      {msg.suggestions.map((suggestion, idx) => (
                        <Chip
                          key={idx}
                          label={suggestion}
                          size="small"
                          onClick={() => handleSuggestionClick(suggestion)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}

              {isLoading && (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    <Sparkles size={16} />
                  </Avatar>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Thinking...
                    </Typography>
                  </Box>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
              <TextField
                id="ai-chat-input"
                fullWidth
                size="small"
                placeholder="Ask me anything..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#F8F9FA',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleSend}
                        color="primary"
                        disabled={!message.trim() || isLoading}
                      >
                        {isLoading ? <CircularProgress size={18} /> : <Send size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                AI responses are for guidance only. Always verify critical information.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Slide>
  );
}

export default GlobalAIChat;
